import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom/client';

import makeCancelable, {CancelablePromise} from 'lib/makeCancelable';
import {Member} from 'orgs/OrganizationConstants';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';
import {AdminSearchPaginatedResponse, doAdminSearchGet} from 'useradmin/UserAdminDAO';
import * as DateUtil from 'util/DateUtil';
import {isEnterKey} from 'util/Keyboard';

import ActivityLogUserSelector from './ActivityLogUserSelector';
import ActivityRow from './ActivityRowTemplate';

class AdminLogView extends Backbone.View<Backbone.Model> {
  _searchPromise?: CancelablePromise<AdminSearchPaginatedResponse>;
  selectedMember: Member | null = null;
  _nextToken: string | null = null;
  _isLoadingMore = false;

  userSelectorReactRoot: ReactDOM.Root | null = null;

  static initClass(): void {
    this.prototype.el = '#activity-log';

    this.prototype.events = {
      // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
      // The Backbone types do not know about this.
      'click .js-refresh': 'makeRequest',
      'click .js-load-more': 'loadMore',
      'keydown .message-search, .date-search input': 'onSearchKeydown',
    };
  }

  onSearchKeydown(e: JQueryKeyEventObject): void {
    if (isEnterKey(e) && e.which !== 229 && !(e.originalEvent as KeyboardEvent | undefined)?.isComposing) {
      e.preventDefault();
      this.makeRequest();
    }
  }

  renderUserSelector(): void {
    this.userSelectorReactRoot = renderToDOM(
      <ActivityLogUserSelector
        organizationId={window.bootstrap!.organization_id!}
        selectedMember={this.selectedMember}
        setSelectedMember={(member) => {
          this.selectedMember = member;
          this.renderUserSelector();
          this.makeRequest();
        }}
      />,
      this.$('.js-select-user')[0],
      this.userSelectorReactRoot
    );
  }

  makeRequest(): void {
    this._nextToken = null;
    this._fetchRecords({replace: true});
  }

  loadMore(): void {
    if (!this._nextToken || this._isLoadingMore) {
      return;
    }
    this._fetchRecords({replace: false});
  }

  _fetchRecords({replace}: {replace: boolean}): void {
    if (replace) {
      this._isLoadingMore = false;
      this.$('.centering-spinner-wrapper').removeClass('hidden');
      this.$('.spinner').removeClass('hidden');
      this.$('tbody').empty();
      this.$('.js-empty-message').addClass('hidden');
      this.$('.js-error-message').addClass('hidden');
      this.$('.js-result-count').addClass('hidden');
      this.$('.js-load-more').prop('disabled', false).addClass('hidden');
    } else {
      this._isLoadingMore = true;
      this.$('.js-load-more').prop('disabled', true);
    }

    const args: {
      nextToken?: string;
      q?: string;
      until?: string;
      user_id?: string;
    } = {};
    const userValue = this.selectedMember?.id;
    if (userValue) {
      args.user_id = userValue;
    }
    const q = this.$('.message-search').val();
    if (q.length !== 0) {
      args.q = q;
    }
    const dateSearch = this.$('.date-search input').val();
    if (dateSearch.length !== 0) {
      args.until = dateSearch;
    }
    if (!replace && this._nextToken) {
      args.nextToken = this._nextToken;
    }

    if (this._searchPromise) {
      this._searchPromise.cancel();
    }
    const searchPromise = makeCancelable(doAdminSearchGet(args));
    this._searchPromise = searchPromise;
    searchPromise.promise
      .then((res) => {
        if (this._searchPromise !== searchPromise) {
          return;
        }
        this._nextToken = res.nextToken || null;
        for (const info of res.records) {
          // eslint-disable-next-line new-cap -- This is a React component wrapped with a Jade compatibility shim
          const row = ActivityRow({
            profile_url: info.profile_url,
            handle: info.handle,
            supportDelegateEmail: info.support_delegate_email,
            text: info.text,
            time: DateUtil.readableTime(new Date(info.created_at)),
          });
          this.$('tbody').append(row);
        }
        this._updatePagingUI();
      })
      .catch((reason) => {
        if (reason?.isCanceled || this._searchPromise !== searchPromise) {
          return;
        }
        this.$('.js-error-message').removeClass('hidden');
      })
      .finally(() => {
        if (this._searchPromise !== searchPromise) {
          return;
        }
        this.$('.centering-spinner-wrapper').addClass('hidden');
        this.$('.spinner').addClass('hidden');
        this._isLoadingMore = false;
        this.$('.js-load-more').prop('disabled', false);
      });
  }

  _updatePagingUI(): void {
    const rowCount = this.$('tbody tr').length;
    if (rowCount === 0) {
      this.$('.js-empty-message').removeClass('hidden');
      this.$('.js-result-count').addClass('hidden');
    } else {
      this.$('.js-empty-message').addClass('hidden');
      const hasMore = Boolean(this._nextToken);
      const countText = hasMore ? `Showing ${rowCount} (more available)` : `Showing ${rowCount}`;
      this.$('.js-result-count').text(countText).removeClass('hidden');
    }
    if (this._nextToken) {
      this.$('.js-load-more').removeClass('hidden');
    } else {
      this.$('.js-load-more').addClass('hidden');
    }
  }

  // @ts-expect-error Backbone render functions should return `this`
  render(): void {
    this.renderUserSelector();
    this.$('.message-search').trigger('focus');
    this.makeRequest();
  }

  override remove(): Backbone.View<Backbone.Model> {
    unmountRootsAsync([this.userSelectorReactRoot], () => {
      this.userSelectorReactRoot = null;
      super.remove();
    });
    // @ts-expect-error Backbone render functions should return `this`
    return this;
  }
}

AdminLogView.initClass();

export default AdminLogView;
